<template>
  <div class="summary-tab">
    <div class="summary-header mb-6">
      <h2 class="text-h5 font-weight-bold mb-2">Talent Ratings Summary</h2>
      <p class="text-body-1 text-medium-emphasis">
        Aggregated satisfaction rates and scores from clients and colleagues
      </p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
      <div class="text-body-1 text-medium-emphasis mt-4">
        Loading ratings data...
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-8">
      <v-icon color="error" size="64" class="mb-4">mdi-alert-circle</v-icon>
      <div class="text-h6 text-error mb-2">Error Loading Data</div>
      <div class="text-body-1 text-medium-emphasis mb-4">{{ error }}</div>
      <v-btn color="primary" @click="fetchData" variant="outlined">
        <v-icon left>mdi-refresh</v-icon>
        Retry
      </v-btn>
    </div>

        <!-- Content -->
    <div v-else>
      <!-- No Data State -->
      <div v-if="!ratingsData || (!avgClientRating && keyStrengths.length === 0 && areasForImprovement.length === 0)" class="text-center py-8">
        <v-icon color="grey" size="64" class="mb-4">mdi-chart-line</v-icon>
        <div class="text-h6 text-grey mb-2">No Ratings Data Available</div>
        <div class="text-body-1 text-medium-emphasis">
          No ratings or feedback data has been collected for this talent yet.
        </div>
      </div>

      <!-- Ratings Data -->
      <div v-else>
        <v-row class="mb-8 equal-height-cards">
      <v-col cols="12" md="4" class="d-flex">
        <v-card class="rating-card pa-6 equal-height-card" elevation="2" style="width: 100%">
          <div class="d-flex align-center mb-4">
            <div class="rating-icon-wrapper mr-4">
              <v-icon color="white" size="32">mdi-account-group</v-icon>
            </div>
            <div>
              <div class="text-subtitle-1 text-medium-emphasis">
                Avg. Satisfaction from Clients
              </div>
              <div class="text-h3 font-weight-bold text-amber mt-1">
                {{ avgClientRating.toFixed(1) }}
              </div>
            </div>
          </div>
          <HalfStarRating :value="avgClientRating" :max="5" color="primary" />
          <div class="rating-stats mt-4">
            <div class="stat-item mb-3">
              <div class="d-flex align-center justify-space-between mb-1">
                <span class="text-caption text-medium-emphasis"
                  >Total Reviews</span
                >
                <span class="text-body-2 font-weight-medium">{{
                   ratingsData.total_ratings || 0
                }}</span>
              </div>
              <v-progress-linear
                :model-value="100"
                color="grey-lighten-3"
                height="4"
                rounded
              ></v-progress-linear>
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="4" class="d-flex">
        <v-card class="rating-card pa-6 equal-height-card" elevation="2"  style="position: relative; width: 100%">
          <div class="coming-soon-overlay">
            <div class="coming-soon-content">
              <v-icon color="white" size="48" class="mb-3"
                >mdi-clock-outline</v-icon
              >
              <div class="text-h6 font-weight-bold text-white mb-1">
                Coming Soon
              </div>
              <div class="text-body-2 text-white text-medium-emphasis">
                This feature is under development
              </div>
            </div>
          </div>
          <div class="d-flex align-center mb-4">
            <div class="rating-icon-wrapper mr-4">
              <v-icon color="white" size="32">mdi-account-multiple</v-icon>
            </div>
            <div>
              <div class="text-subtitle-1 text-medium-emphasis">
                Avg. Satisfaction from Colleagues
              </div>
              <div class="text-h3 font-weight-bold text-primary mt-1">
                {{ avgColleagueRating.toFixed(1) }}
              </div>
            </div>
          </div>
          <v-rating
            v-model="avgColleagueRating"
            readonly
            dense
            color="primary"
            size="28"
            class="mb-4"
          ></v-rating>
        </v-card>
      </v-col>
      <v-col cols="12" md="4" class="d-flex">
        <v-card class="rating-card pa-6 equal-height-card" elevation="2"   style="position: relative; width: 100%">
          <div class="coming-soon-overlay">
            <div class="coming-soon-content">
              <v-icon color="white" size="48" class="mb-3"
                >mdi-clock-outline</v-icon
              >
              <div class="text-h6 font-weight-bold text-white mb-1">
                Coming Soon
              </div>
              <div class="text-body-2 text-white text-medium-emphasis">
                This feature is under development
              </div>
            </div>
          </div>
          <div class="d-flex align-center mb-4">
            <div class="rating-icon-wrapper mr-4">
              <v-icon color="white" size="32">mdi-star-circle</v-icon>
            </div>
            <div>
              <div class="text-subtitle-1 text-medium-emphasis">
                Overall Avg. Satisfaction
              </div>
              <div class="text-h3 font-weight-bold text-success mt-1">
                {{ avgSatisfactionScore.toFixed(1) }}
              </div>
            </div>
          </div>
          <v-rating
            v-model="avgSatisfactionScore"
            readonly
            dense
            color="success"
            size="28"
            class="mb-4"
          ></v-rating>
        </v-card>
      </v-col>
    </v-row>
    <!-- Review Points Summary -->
    <div class="summary-section mb-6">
      <h3 class="text-h6 font-weight-bold mb-4">Review Points Summary</h3>
      <v-row class="equal-height-cards">
        <v-col cols="12" md="6">
          <v-card class="insight-card mb-4 equal-height-card" elevation="2">
            <v-card-title class="d-flex align-center pa-6 pb-4">
              <div class="d-flex align-center">
                <div class="icon-wrapper success mr-4">
                  <v-icon color="white" size="24">mdi-thumb-up</v-icon>
                </div>
                <div>
                  <div class="text-h6 font-weight-bold">Key Strengths</div>
                  <div class="text-caption text-medium-emphasis">
                    Most frequently mentioned positive attributes
                  </div>
                </div>
              </div>
              <v-spacer></v-spacer>
            </v-card-title>
            <v-card-text class="pa-6 pt-0">
              <div v-if="keyStrengths.length > 0" class="chip-list">
                <v-chip
                  v-for="(strength, index) in keyStrengths"
                  :key="'strength-' + index"
                  color="success"
                  variant="tonal"
                  class="ma-1 font-weight-medium strength-chip"
                  size="large"
                  pill
                >
                  <v-icon left size="small" color="success"
                    >mdi-check-circle</v-icon
                  >
                  {{ strength }}
                </v-chip>
              </div>
              <div v-else class="text-center py-4">
                <v-icon color="grey" size="32" class="mb-2">mdi-information</v-icon>
                <div class="text-body-2 text-medium-emphasis">No strengths identified yet</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card class="insight-card mb-4 equal-height-card" elevation="2">
            <v-card-title class="d-flex align-center pa-6 pb-4">
              <div class="d-flex align-center">
                <div class="icon-wrapper error mr-4">
                  <v-icon color="white" size="24">mdi-alert-circle</v-icon>
                </div>
                <div>
                  <div class="text-h6 font-weight-bold">
                    Areas for Improvement
                  </div>
                  <div class="text-caption text-medium-emphasis">
                    Most frequently mentioned areas to enhance
                  </div>
                </div>
              </div>
              <v-spacer></v-spacer>
            </v-card-title>
            <v-card-text class="pa-6 pt-0">
              <div v-if="areasForImprovement.length > 0" class="chip-list">
                <v-chip
                  v-for="(area, index) in areasForImprovement"
                  :key="'area-' + index"
                  color="error"
                  variant="tonal"
                  class="ma-1 font-weight-medium improvement-chip"
                  size="large"
                  pill
                >
                  <v-icon left size="small" color="error">mdi-alert</v-icon>
                  {{ area }}
                </v-chip>
              </div>
              <div v-else class="text-center py-4">
                <v-icon color="grey" size="32" class="mb-2">mdi-information</v-icon>
                <div class="text-body-2 text-medium-emphasis">No areas for improvement identified yet</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
    </div>
    </div>
  </div>
</template>
<script>
import { mapActions } from "vuex";
import HalfStarRating from '@/components/shared/HalfStarRating.vue';

export default {
  name: "TalentRatingsSummaryTab",
  components: {
    HalfStarRating,
  },
  props: {
    talentId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      // API response data
      ratingsData: null,
      loading: false,
      error: null,
    };
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    ...mapActions({
      getTalentRatings: "talents/getTalentRatings",
    }),
    async fetchData() {
      this.loading = true;
      this.error = null;

      try {
        const res = await this.getTalentRatings({
          rater_id: this.$route.params.id,
          rater_type: "talent",
          summary: true,
        });

        this.ratingsData = res;
        console.log(res);
      } catch (error) {
        console.error('Error fetching ratings data:', error);
        this.error = 'Failed to load ratings data';
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    avgClientRating() {
      if (!this.ratingsData || !this.ratingsData.avg_client_to_talent) return 0;
      return this.ratingsData.avg_client_to_talent;
    },
    avgColleagueRating() {
      // Set to 0 for now since API doesn't provide colleague rating
      return 0;
    },
    avgSatisfactionScore() {
      // Set to 0 for now
      return 0;
    },
    keyStrengths() {
      if (!this.ratingsData || !this.ratingsData.options || !this.ratingsData.options.positive) {
        return [];
      }
      return this.ratingsData.options.positive.map(item => item.label);
    },
    areasForImprovement() {
      if (!this.ratingsData || !this.ratingsData.options || !this.ratingsData.options.negative) {
        return [];
      }
      return this.ratingsData.options.negative.map(item => item.label);
    },
  },
};

</script>
<style scoped>
.summary-tab {
  padding: 1.5rem;
}
.summary-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding-bottom: 1rem;
}
.rating-card {
  border-radius: 16px;
  background: linear-gradient(to bottom right, #ffffff, #f8fafc);
  border: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
}
.rating-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}
.rating-icon-wrapper .v-icon {
  color: white !important;
}
.text-amber {
  color: #f59e0b !important;
}
.text-primary {
  color: #3b82f6 !important;
}
.text-success {
  color: #10b981 !important;
}
.insight-card {
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(0, 0, 0, 0.05);
}
.icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}
.icon-wrapper.success {
  background: linear-gradient(135deg, #34d399 0%, #059669 100%);
}
.icon-wrapper.error {
  background: linear-gradient(135deg, #f87171 0%, #dc2626 100%);
}
.v-card-title {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0.9),
    rgba(248, 250, 252, 0.9)
  );
}
.chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}
.strength-chip {
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%) !important;
  color: #166534 !important;
  border: 1px solid #86efac !important;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.1);
}
.improvement-chip {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%) !important;
  color: #991b1b !important;
  border: 1px solid #fca5a5 !important;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.1);
}
.v-chip {
  border: 1px solid transparent;
  font-weight: 500;
}
.v-chip .v-icon {
  margin-right: 4px;
}

.equal-height-cards {
  display: flex;
}

.equal-height-cards .v-col {
  display: flex;
}

.equal-height-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.equal-height-card .v-card-text {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.coming-soon-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  cursor: not-allowed;
}

.coming-soon-content {
  text-align: center;
  padding: 2rem;
}
.rating-stats {
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

	.stat-item {
  margin-bottom: 1rem;
}

</style>
